import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";

const eur = v => v == null ? "—" : `€${Math.round(v).toLocaleString("it-IT")}`;
const fmt = (v, u) => v == null ? "—" : (u === "%" ? `${Number(v).toLocaleString("it-IT", { maximumFractionDigits: 1 })}%` : eur(v));
const STATE_UI = {
  PUNTO_DI_FORZA: ["Punto di forza", "#10B981"], IN_LINEA: ["In linea", "#5424D5"],
  DA_MONITORARE: ["Da monitorare", "#FF6A24"], SENZA_CONFRONTO: ["Senza confronto benchmark", "#7A7788"],
};

export default function AnalisiFinanziaria() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/analysis/full").then(r => setData(r.data)).catch(e => {
      setErr(String(formatApiError(e)).includes("upgrade_required") ? "locked" : "generic");
    });
  }, []);

  if (err === "locked") return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="af-locked">
      <h1 className="text-3xl font-extrabold">Analisi Economico-Finanziaria</h1>
      <p className="text-[#524F5E] mt-3">La riclassificazione completa con benchmark BACH è disponibile dal piano <b>CONTROL</b>.</p>
      <Link to="/pricing" className="ic-btn-primary inline-block mt-6" data-testid="af-pricing">Vedi i piani</Link>
    </div>
  );
  if (err) return <div className="max-w-3xl mx-auto px-4 py-20 text-center">Errore di caricamento.</div>;
  if (!data) return <div className="max-w-6xl mx-auto px-4 py-20 text-center">Caricamento analisi…</div>;
  if (data.status === "INSUFFICIENT_DATA") return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="af-empty">
      <h1 className="text-3xl font-extrabold">Analisi Economico-Finanziaria</h1>
      <p className="text-[#524F5E] mt-3">{data.message} Per attivare l'analisi servono i dati economici dell'ultimo esercizio.</p>
      <Link to="/report" className="ic-btn-primary inline-block mt-6" data-testid="af-complete">Completa i dati</Link>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="af-page">
      <p className="text-xs uppercase tracking-widest text-[#FF6A24] font-bold">Analisi Economico-Finanziaria</p>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Conto economico riclassificato</h1>
      <p className="text-sm text-[#7A7788] mt-2">Periodo {data.period_id} · Financial Engine v{data.engine_version}
        {data.cogs_status === "SECTOR_PROXY" && " · COGS: stima di settore"}</p>

      <div className="ic-card overflow-x-auto mt-8 mb-10" data-testid="af-pnl">
        <table className="w-full text-sm">
          <tbody>
            {data.pnl.map(r => (
              <tr key={r.key} className={`border-t border-[#F4F4F7] first:border-0 ${["ebitda", "ebit", "net_income"].includes(r.key) ? "font-bold bg-[#FAFAFC]" : ""}`} data-testid={`af-row-${r.key}`}>
                <td className="p-3 text-[#524F5E]">{r.label}</td>
                <td className="p-3 text-right font-semibold">{r.value != null ? fmt(r.value, r.unit) : <span className="text-[#7A7788] font-normal text-xs">dato non inserito</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="text-xl font-bold mb-4">Indicatori per area · confronto {data.benchmark_source}</h2>
      <div className="grid md:grid-cols-2 gap-5">
        {data.sections.map(s => {
          const st = STATE_UI[s.state] || STATE_UI.SENZA_CONFRONTO;
          return (
            <div key={s.section_id} className="ic-card p-6" data-testid={`af-section-${s.section_id}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold">{s.label}</h3>
                <span className="ic-badge" style={{ color: st[1] }} data-testid={`af-state-${s.section_id}`}>{st[0]}</span>
              </div>
              <div className="space-y-3">
                {s.kpis.map(k => (
                  <div key={k.kpi_code} className="flex justify-between items-start gap-3 border-b border-[#F4F4F7] pb-2 last:border-0" data-testid={`af-kpi-${k.kpi_code}`}>
                    <div>
                      <p className="text-sm font-semibold">{k.label}</p>
                      {k.insight && <p className="text-xs text-[#7A7788] mt-0.5">{k.insight}</p>}
                      {!k.benchmark_available && k.actual != null && (
                        <p className="text-xs text-[#7A7788] mt-0.5">Benchmark non disponibile per questo indicatore</p>
                      )}
                    </div>
                    <p className="text-sm font-bold text-[#5424D5] whitespace-nowrap">
                      {k.actual != null ? `${Number(k.actual).toLocaleString("it-IT", { maximumFractionDigits: 2 })}${k.unit === "%" ? "%" : ""}` : "—"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-[#7A7788] mt-6">Interpretazioni derivate solo dai dati inseriti e dai benchmark disponibili. Non sostituiscono la consulenza professionale.</p>
    </div>
  );
}
